import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { Button, Card, Field, Input, Select, Textarea, Tag, dataBR } from "../ui";

const TIPOS = [
    { valor: "info", rotulo: "Informativo" },
    { valor: "aviso", rotulo: "Aviso" },
    { valor: "urgente", rotulo: "Urgente" }
];

const TOM = { info: "accent", aviso: "warn", urgente: "danger" };

const VAZIO = { cod_comunicado: null, titulo: "", mensagem: "", tipo: "info", expira_em: "" };

/** Aba "Comunicados" — avisos publicados na Início de TODOS os escritórios. */
const AdminComunicados = ({ avisar }) => {
    const [comunicados, setComunicados] = useState([]);
    const [form, setForm] = useState(VAZIO);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState(null);

    const carregar = () => api.get("/admin/comunicados").then((r) => setComunicados(r.data)).catch(() => {});

    useEffect(() => { carregar(); }, []);

    const mudar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

    const salvar = async () => {
        if (!form.titulo.trim() || !form.mensagem.trim()) { setErro("Preencha título e mensagem."); return; }
        setSalvando(true); setErro(null);
        const corpo = { ...form, expira_em: form.expira_em || null };
        try {
            if (form.cod_comunicado) await api.put(`/admin/comunicados/${form.cod_comunicado}`, corpo);
            else await api.post("/admin/comunicados", corpo);
            avisar?.(form.cod_comunicado ? "Comunicado salvo." : "Comunicado publicado para todos os escritórios.");
            setForm(VAZIO);
            carregar();
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível salvar o comunicado.");
        } finally {
            setSalvando(false);
        }
    };

    const alternar = async (com) => {
        try {
            await api.patch(`/admin/comunicados/${com.cod_comunicado}`, { ativo: !com.ativo });
            carregar();
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível alterar o comunicado.");
        }
    };

    const excluir = async (com) => {
        if (!window.confirm(`Excluir o comunicado "${com.titulo}"?`)) return;
        try {
            await api.delete(`/admin/comunicados/${com.cod_comunicado}`);
            if (form.cod_comunicado === com.cod_comunicado) setForm(VAZIO);
            carregar();
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível excluir.");
        }
    };

    const editar = (com) => setForm({
        cod_comunicado: com.cod_comunicado,
        titulo: com.titulo,
        mensagem: com.mensagem,
        tipo: com.tipo,
        expira_em: com.expira_em || ""
    });

    const hoje = new Date().toISOString().slice(0, 10);
    const expirado = (com) => !!com.expira_em && com.expira_em < hoje;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {erro && <p className="erro">{erro}</p>}

            <Card>
                <h3 className="card-title">{form.cod_comunicado ? "Editar comunicado" : "Novo comunicado"}</h3>
                <p className="card-body text-muted" style={{ margin: 0 }}>
                    O aviso aparece no topo da tela Início de todos os escritórios, e cada usuário pode dispensá-lo.
                </p>
                <div className="grid-form">
                    <Field label="Título" className="span-2">
                        <Input maxLength={120} value={form.titulo} onChange={(e) => mudar("titulo", e.target.value)} />
                    </Field>
                    <Field label="Tipo">
                        <Select value={form.tipo} onChange={(e) => mudar("tipo", e.target.value)}>
                            {TIPOS.map((t) => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
                        </Select>
                    </Field>
                    <Field label="Expira em (opcional)">
                        <Input type="date" value={form.expira_em} onChange={(e) => mudar("expira_em", e.target.value)} />
                    </Field>
                    <Field label="Mensagem (até 500 caracteres)" className="span-2">
                        <Textarea maxLength={500} style={{ minHeight: 110 }} value={form.mensagem}
                            onChange={(e) => mudar("mensagem", e.target.value)} />
                    </Field>
                </div>
                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                    <Button onClick={salvar} disabled={salvando}>
                        {salvando ? "Salvando..." : form.cod_comunicado ? "Salvar" : "Publicar"}
                    </Button>
                    {form.cod_comunicado && <Button variante="secondary" onClick={() => setForm(VAZIO)}>Cancelar</Button>}
                </div>
            </Card>

            <div className="scroll-x">
                <table className="table">
                    <thead>
                        <tr><th>Título</th><th>Tipo</th><th>Publicado</th><th>Expira</th><th>Situação</th><th>Ações</th></tr>
                    </thead>
                    <tbody>
                        {comunicados.map((com) => (
                            <tr key={com.cod_comunicado}>
                                <td>
                                    {com.titulo}<br />
                                    <span className="text-muted" style={{ fontSize: 13.5 }}>{com.mensagem}</span>
                                </td>
                                <td><Tag tom={TOM[com.tipo]}>{com.tipo}</Tag></td>
                                <td className="num">{dataBR(com.criado_em)}</td>
                                <td className="num">{com.expira_em ? dataBR(com.expira_em) : "—"}</td>
                                <td>
                                    <Tag tom={com.ativo && !expirado(com) ? "success" : "neutral"}>
                                        {!com.ativo ? "oculto" : expirado(com) ? "expirado" : "no ar"}
                                    </Tag>
                                </td>
                                <td>
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                        <Button variante="secondary" onClick={() => editar(com)}>Editar</Button>
                                        <Button variante="ghost" onClick={() => alternar(com)}>
                                            {com.ativo ? "Desativar" : "Ativar"}
                                        </Button>
                                        <Button variante="danger" onClick={() => excluir(com)}>Excluir</Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {comunicados.length === 0 && (
                            <tr><td colSpan="6" className="text-muted">Nenhum comunicado publicado.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminComunicados;
